import { UserProperty } from 'utils/analytics/eventTypesConstants';
import { customDimensions, pathSanitizer } from 'utils/Analytics';
import { ga4AddScript, ga4SetupTracking, ga4SetupTrackingRestaurant } from 'utils/analytics/ga4';
import {
  getApp,
  getBusinessAnalyticsPropertiesSelector,
  getEnv,
  getTockGAPropertiesSelector,
} from 'selectors/appSelector';
import { googleAnalyticsScriptsLoadedSelector } from 'selectors/cookieConsentSelectors';
import { logger } from '@tock/logging';
import { recordWebVitals } from 'utils/webVitalsGa';
import {
  uaAddScript,
  uaSetupTracking,
  uaSetupTrackingRestaurant,
  uaTransitionToPage,
} from 'utils/analytics/ua';
import { useActions } from '@tock/shared/utils/connect';
import { useConsentRestrictedScripts } from 'hooks/useConsentRestrictedScripts';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useSelector } from 'react-redux';
import React, { useCallback, useState } from 'react';
import _ from 'lodash';

const getRestaurantDomain = (restaurantWebsite?: string | null): string =>
  restaurantWebsite ? _.split(_.replace(restaurantWebsite, /https?:\/\//, ''), '/')[0] : '';

const getTrackedDomains = (
  restaurantDomain: string,
  trackedDomainsString?: string | null
): string[] =>
  _.uniq(
    _.filter(_.map(_.split(trackedDomainsString || '', ',').concat(restaurantDomain), _.trim))
  );

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const gaWrapperDefault = (...args: any[]) => {
  logger.debug('Analytics.gaWrapper', ...args);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const gtagWrapperDefault = (...args: any[]) => {
  logger.debug('Analytics.gtagWrapper', ...args);
};

// IMPORTANT - getGaWrapper must return a function - we must attempt to access the window.ga function when called
export const getGaWrapper = () => {
  if (window.GoogleAnalyticsObject) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (window as any)[window.GoogleAnalyticsObject];
  }
  return gaWrapperDefault;
};

// IMPORTANT - getGtagWrapper must return a function - we must attempt to access the window.gtag function when called
export const getGtagWrapper = () => {
  if (window.gtag) {
    return window.gtag;
  }
  return gtagWrapperDefault;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ga4SendEvent = (event: string, properties: Dictionary<any>) => {
  const sendToRestaurant = location.hostname.indexOf('staging.exploretock.com') < 0;
  getGtagWrapper()('event', event, {
    ...properties,
    send_to: ['default', ...(sendToRestaurant ? ['restaurant'] : [])],
  });
};

export const ga4SetUserProperty = (userProperty: UserProperty, specificStreamId?: string) => {
  if (specificStreamId) {
    getGtagWrapper()('config', specificStreamId, {
      user_properties: {
        [userProperty.property]: userProperty.value,
      },
    });
  } else {
    getGtagWrapper()('config', 'user_properties', { [userProperty.property]: userProperty.value });
  }
};

function setupUA(
  isLegacySetup: boolean,
  fastRoutingBetweenContext: boolean,
  webPropertyGA: string,
  trackedDomains: string[]
) {
  if (isLegacySetup) {
    logger.log('Setting up legacy UA GA');
  } else {
    logger.log('Setting up parallel UA GA');
  }

  uaSetupTrackingRestaurant(
    getGaWrapper(),
    fastRoutingBetweenContext,
    webPropertyGA,
    trackedDomains
  );
}

function setupGA4(
  isLegacySetup: boolean,
  fastRoutingBetweenContext: boolean,
  webPropertyGA4: string,
  trackedDomains: string[]
) {
  if (isLegacySetup) {
    logger.log('Setting up legacy GA4');
  } else {
    logger.log('Setting up parallel GA4');
  }

  ga4SetupTrackingRestaurant(
    getGtagWrapper(),
    fastRoutingBetweenContext,
    webPropertyGA4,
    trackedDomains
  );
}

/**
 * Adds the Google Analytics script and sets up tracking for Tock + customer properties
 * CAUTION: Execution order in this file is very important to allow proper script loading prior to
 * initializing properties and / or sending pageviews and events
 */
export const GoogleAnalytics: React.FC = () => {
  const { ga4Key, uaKey, optimizeId } = useSelector(getTockGAPropertiesSelector);
  const {
    businessWebProperty,
    businessWebPropertyGA4,
    businessWebPropertyUa,
    trackedDomains: trackedDomainsString,
    webUrl: businessWebUrl,
  } = useSelector(getBusinessAnalyticsPropertiesSelector);
  const { fastRoutingBetweenContext } = useSelector(getApp);
  const currEnv = useSelector(getEnv);
  const scriptAdded = useSelector(googleAnalyticsScriptsLoadedSelector);
  const { actions } = useActions();

  const shouldSendRestaurantPixel = currEnv !== 'staging';
  const restaurantUsingLatestGA: boolean =
    businessWebProperty != null && businessWebProperty.includes('G-');
  const trackedDomains = getTrackedDomains(
    getRestaurantDomain(businessWebUrl),
    trackedDomainsString
  );
  const isBusinessConfigPropertySet = !!(businessWebPropertyGA4 || businessWebPropertyUa);
  const { consentLevelAdvertising, consentLevelFunctional } = useConsentRestrictedScripts();
  const [businessAnalyticsInitialized, setBusinessAnalyticsInitialized] = useState(false);

  const addGaTrackToWindow = useCallback(() => {
    // add gaTrack to window
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const gaTrack = (...args: any[]) => {
      getGaWrapper()(...args);

      if (
        !fastRoutingBetweenContext &&
        businessWebProperty &&
        shouldSendRestaurantPixel &&
        !restaurantUsingLatestGA
      ) {
        args[0] = 'restaurant.' + args[0];
        getGaWrapper()(...args);
      }
    };

    window.analytics.gaTrack = gaTrack;

    return gaTrack;
  }, [
    businessWebProperty,
    fastRoutingBetweenContext,
    restaurantUsingLatestGA,
    shouldSendRestaurantPixel,
  ]);

  const applyAnalyticsReferrerFix = useFeatureFlag('SOCIAL_LOGIN_ANALYTICS_REFERRER_FIX');

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const windowObj = window as any; // GA window properties are dynamic based on id so this is required
    const businessAnalyticsEnabled =
      shouldSendRestaurantPixel &&
      (businessWebProperty || businessWebPropertyGA4 || businessWebPropertyUa);

    if (consentLevelFunctional) {
      if (!scriptAdded) {
        uaAddScript(optimizeId);
        ga4AddScript(ga4Key);
        uaSetupTracking(
          getGaWrapper(),
          uaKey,
          optimizeId,
          customDimensions,
          consentLevelAdvertising,
          applyAnalyticsReferrerFix
        );
        ga4SetupTracking(getGtagWrapper(), ga4Key, customDimensions, consentLevelAdvertising);
      }

      // Setup Restaurant tracking
      if (businessAnalyticsEnabled) {
        if (!businessAnalyticsInitialized) {
          if (isBusinessConfigPropertySet) {
            if (businessWebPropertyGA4) {
              setupGA4(false, fastRoutingBetweenContext, businessWebPropertyGA4, trackedDomains);
            }
            if (businessWebPropertyUa) {
              setupUA(false, fastRoutingBetweenContext, businessWebPropertyUa, trackedDomains);
            }
          } else {
            if (businessWebProperty) {
              if (restaurantUsingLatestGA) {
                setupGA4(true, fastRoutingBetweenContext, businessWebProperty, trackedDomains);
              } else {
                setupUA(true, fastRoutingBetweenContext, businessWebProperty, trackedDomains);
              }
            }
          }
          setBusinessAnalyticsInitialized(true);
        }
        windowObj[`ga-disable-${businessWebProperty}`] = false;
      }

      // Second !scriptAdded block necessary to ensure that business property is initialized (if needed) prior to executing transitionToPage()
      if (!scriptAdded) {
        const gaTrack = addGaTrackToWindow();
        actions.cookieConsent.updateScriptLoad(true);
        if (BROWSER) {
          uaTransitionToPage(gaTrack, pathSanitizer(location), location.origin);
          recordWebVitals();
        }
      }

      windowObj[`ga-disable-${ga4Key}`] = false;
      windowObj[`ga-disable-${uaKey}`] = false;
    } else {
      windowObj[`ga-disable-${ga4Key}`] = true;
      windowObj[`ga-disable-${uaKey}`] = true;

      if (businessAnalyticsEnabled) {
        windowObj[`ga-disable-${businessWebProperty}`] = true;
      }
    }
  }, [
    actions.cookieConsent,
    consentLevelFunctional,
    uaKey,
    ga4Key,
    optimizeId,
    shouldSendRestaurantPixel,
    businessWebProperty,
    restaurantUsingLatestGA,
    fastRoutingBetweenContext,
    trackedDomains,
    addGaTrackToWindow,
    scriptAdded,
    businessAnalyticsInitialized,
    consentLevelAdvertising,
    applyAnalyticsReferrerFix,
    businessWebPropertyUa,
    businessWebPropertyGA4,
    isBusinessConfigPropertySet,
  ]);

  return null;
};
